import { useState, useEffect } from 'react';
import AnimatedPage from '@/components/AnimatedPage';
import { GlassCard, GlassCardContent, GlassCardDescription, GlassCardHeader, GlassCardTitle } from '@easyflow/ui-master/components/ui/glass-card';
import { Badge } from '@easyflow/ui-master/components/ui/badge';
import { Button } from '@easyflow/ui-master/components/ui/button';
import { Input } from '@easyflow/ui-master/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@easyflow/ui-master/components/ui/table';
import { SkeletonMetricGrid, SkeletonTable } from '@easyflow/ui-master/components/ui/skeleton-loader';
import { Server, Building2, Activity, RefreshCw, Search, Phone, Signal } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import apiClient from '@/lib/api';
import type { MasterTrunkRow as TrunkData, MasterTrunkStats as TrunkStats } from '@easyflow/shared';
import { toErr } from '@easyflow/shared';

export default function TrunksMaster() {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [trunks, setTrunks] = useState<TrunkData[]>([]);
  const [stats, setStats] = useState<TrunkStats | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    void loadData();
  }, []);

  const loadData = async () => {
    try {
      setIsLoading(true);

      const [trunksRes, statsRes] = await Promise.all([
        apiClient.get<TrunkData[]>('/master/trunks'),
        apiClient.get<TrunkStats>('/master/trunks/stats'),
      ]);

      setTrunks(Array.isArray(trunksRes.data) ? trunksRes.data : []);
      setStats(statsRes.data ?? null);
    } catch (error: unknown) {
      const __error = toErr(error);
      toast({
        title: 'Error',
        description: __error.message || 'Failed to load trunks',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const filteredTrunks = trunks.filter((trunk) => {
    const search = searchTerm.toLowerCase();
    return trunk.name.toLowerCase().includes(search) ||
      trunk.host.toLowerCase().includes(search) ||
      trunk.tenant_name.toLowerCase().includes(search) ||
      trunk.username.toLowerCase().includes(search) ||
      (trunk.legal_name || '').toLowerCase().includes(search) ||
      (trunk.vat_number || '').toLowerCase().includes(search);
  });

  const getStatusBadge = (status: string) => {
    return status === 'active' ? (
      <Badge className="bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300">Active</Badge>
    ) : (
      <Badge variant="secondary">Inactive</Badge>
    );
  };

  const getTrunkTypeBadge = (type: string) => {
    const colors = {
      sip: 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300',
      iax2: 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300',
    };
    return (
      <Badge className={colors[type as keyof typeof colors] || 'bg-muted text-foreground'}>
        {type.toUpperCase()}
      </Badge>
    );
  };

  if (isLoading) {
    return (
      <div className="space-y-6 p-6">
        <div className="mb-6">
          <div className="h-10 w-96 bg-white/20 rounded animate-pulse mb-2" />
          <div className="h-4 w-128 bg-white/10 rounded animate-pulse" />
        </div>
        <SkeletonMetricGrid cols={4} />
        <SkeletonTable rows={10} />
      </div>
    );
  }

  return (
    <AnimatedPage>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              SIP Trunks Management
            </h1>
            <p className="text-muted-foreground dark:text-muted-foreground/70 mt-1">Cross-tenant trunk overview and monitoring</p>
          </div>
          <Button onClick={loadData} variant="outline">
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </Button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <GlassCard className="border-l-4 border-l-blue-500">
              <GlassCardHeader className="pb-3">
                <GlassCardDescription className="flex items-center gap-2">
                  <Server className="h-4 w-4" />
                  Total Trunks
                </GlassCardDescription>
                <GlassCardTitle className="text-3xl">{stats.total_trunks}</GlassCardTitle>
              </GlassCardHeader>
            </GlassCard>

            <GlassCard className="border-l-4 border-l-green-500">
              <GlassCardHeader className="pb-3">
                <GlassCardDescription className="flex items-center gap-2">
                  <Activity className="h-4 w-4" />
                  Active Trunks
                </GlassCardDescription>
                <GlassCardTitle className="text-3xl text-green-600">{stats.active_trunks}</GlassCardTitle>
              </GlassCardHeader>
            </GlassCard>

            <GlassCard className="border-l-4 border-l-purple-500">
              <GlassCardHeader className="pb-3">
                <GlassCardDescription className="flex items-center gap-2">
                  <Building2 className="h-4 w-4" />
                  Tenants with Trunks
                </GlassCardDescription>
                <GlassCardTitle className="text-3xl">{stats.tenants_with_trunks}</GlassCardTitle>
              </GlassCardHeader>
            </GlassCard>

            <GlassCard className="border-l-4 border-l-orange-500">
              <GlassCardHeader className="pb-3">
                <GlassCardDescription className="flex items-center gap-2">
                  <Phone className="h-4 w-4" />
                  Total Channels
                </GlassCardDescription>
                <GlassCardTitle className="text-3xl text-orange-600">
                  {stats.total_max_channels}
                </GlassCardTitle>
              </GlassCardHeader>
            </GlassCard>
          </div>
        )}

        {/* Trunks Table */}
        <GlassCard>
          <GlassCardHeader>
            <div className="flex items-center gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground/70 dark:text-muted-foreground h-4 w-4" />
                <Input
                  placeholder="Search by trunk name, host, tenant, or username..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
          </GlassCardHeader>
          <GlassCardContent>
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Trunk Name</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Tenant</TableHead>
                    <TableHead>Host</TableHead>
                    <TableHead>Username</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-center">Max Channels</TableHead>
                    <TableHead>Created</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTrunks.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8 text-muted-foreground dark:text-muted-foreground/70">
                        No trunks found
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredTrunks.map((trunk) => (
                      <TableRow key={trunk.id} className="hover:bg-muted/50 dark:hover:bg-white/5">
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            <Signal className="h-4 w-4 text-muted-foreground/70 dark:text-muted-foreground" />
                            {trunk.name}
                          </div>
                        </TableCell>
                        <TableCell>{getTrunkTypeBadge(trunk.trunk_type)}</TableCell>
                        <TableCell>
                          <div>
                            <div className="font-medium">{trunk.tenant_name}</div>
                            <div className="text-sm text-muted-foreground dark:text-muted-foreground/70">{trunk.tenant_slug}</div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <code className="px-2 py-1 bg-muted rounded text-sm">
                            {trunk.host}:{trunk.port}
                          </code>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm text-muted-foreground dark:text-muted-foreground/60">{trunk.username}</span>
                        </TableCell>
                        <TableCell>{getStatusBadge(trunk.status)}</TableCell>
                        <TableCell className="text-center">
                          <span className="font-semibold text-blue-600">
                            {trunk.max_channels}
                          </span>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm text-muted-foreground dark:text-muted-foreground/60">
                            {new Date(trunk.created_at).toLocaleDateString()}
                          </span>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </GlassCardContent>
        </GlassCard>
      </div>
    </AnimatedPage>
  );
}
